import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { handleDomBubbleEvent } from '@/utils/dom';
import { queryClient } from '@/lib/react-query';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { SetAsAdminValidationSchema, TSetAsAdminValidation, useSetAsAdmin } from '../../../api/set-as-admin.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { GET_LIST_AGENT_QUERY_KEY } from '../../../api/get-list-agent-api';
import { GET_DETAIL_AGENT_QUERY_KEY } from '../../../api/get-detail-agent.api';
import type { AxiosErrorCustom } from '@/lib/axios';

export type TSetAsAdminAgentModalType = 'SetAsAdminAgentModal';
export interface ISetAsAdminAgentModalProps {
  agentId: string;
}

const SetAsAdminAgentModal = memo(({ agentId }: ISetAsAdminAgentModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();
  const { agentId: routeAgentId } = useParams<{ agentId: string }>();

  const isOpenSetAsAdminAgentModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('SetAsAdminAgentModal');
  }, [userModalRootActions]);

  const form = useForm<TSetAsAdminValidation>({
    resolver: zodResolver(SetAsAdminValidationSchema),
    mode: 'onChange',
    defaultValues: {
      agentId: agentId || '',
    },
  });

  useEffect(() => {
    if (agentId) {
      form.reset({
        agentId: agentId || '',
      });
    }
  }, [agentId, form]);

  const mutationSetAsAdmin = useSetAsAdmin({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error set as admin, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: (_, variables) => {
        // Prioritize routeAgentId since that's what the detail page query uses
        // Fallback to mutation variable or prop if route param is not available
        const agentIdToInvalidate = routeAgentId || variables.agentId || agentId;
        if (agentIdToInvalidate) {
          Promise.all([
            queryClient.invalidateQueries({ queryKey: [GET_DETAIL_AGENT_QUERY_KEY, { agentId: agentIdToInvalidate }] }),
            queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY] }),
          ]);
        } else {
          // Fallback: invalidate all detail agent queries if we can't determine the specific agentId
          queryClient.invalidateQueries({ queryKey: [GET_DETAIL_AGENT_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY] });
        }
        userModalRootActions.closeModal('SetAsAdminAgentModal');
        showToast({
          message: 'Set as admin successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitSetAsAdmin = useCallback(
    (data: TSetAsAdminValidation) => {
      if (!data.agentId) {
        showToast({ type: 'error', message: 'Agent not found!' });
        return;
      }
      mutationSetAsAdmin.mutate({
        agentId: data.agentId,
      });
    },
    [mutationSetAsAdmin, showToast]
  );

  const handleClose = useCallback(() => {
    userModalRootActions.closeModal('SetAsAdminAgentModal');
  }, [userModalRootActions]);

  return (
    <Modal isOpen={isOpenSetAsAdminAgentModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[406px]">
      <ModalHeader>
        <ModalTitle>Are you sure want to set as Admin?</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitSetAsAdmin)(e);
          }}
          id="set-as-admin-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">
            You can no longer interact directly with customers or receive chats like before.
          </span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="set-as-admin-form" type="submit" disabled={mutationSetAsAdmin.isPending}>
          {mutationSetAsAdmin.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Okay
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

SetAsAdminAgentModal.displayName = 'SetAsAdminAgentModal';
export default SetAsAdminAgentModal;
